// Import necessary hooks and types
import { useState, useEffect } from 'react';
import { Job } from '../types/job'; // Type definition for jobs
import { useAuth } from './useAuth'; // Hook for authentication context

/**
 * Custom hook to fetch and manage jobs assigned to the current responsible user.
 */
export function useResponsibleJobs() {
  // State to manage jobs, loading status, and potential errors
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Extract authentication information from useAuth
  const { token, user } = useAuth();

  // Function to fetch jobs associated with the responsible user
  const fetchResponsibleJobs = async () => {
    if (!token || !user) {
      setError('Authentication required');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Step 1: Fetch responsible data for the logged-in user
      const responsiblesResponse = await fetch(
        'https://x1lc-aku3-stjs.p7.xano.io/api:O20NksVB/responsibles',
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (!responsiblesResponse.ok) {
        throw new Error('Failed to fetch responsibles data');
      }

      const responsibles = await responsiblesResponse.json();

      // Match the logged-in user with a responsible entity
      const currentResponsible = responsibles.find(
        (r: any) =>
          r.email?.toLowerCase() === user.email?.toLowerCase() || // Match by email (case insensitive)
          r.full_name?.toLowerCase() === user.fullName?.toLowerCase() // Match by full name
      );

      if (!currentResponsible) {
        setError(`No responsible found matching email ${user.email} or name ${user.fullName}`);
        setJobs([]);
        return;
      }

      // Step 2: Fetch all jobs
      const jobsResponse = await fetch(
        'https://x1lc-aku3-stjs.p7.xano.io/api:O20NksVB/job',
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (!jobsResponse.ok) {
        throw new Error('Failed to fetch jobs');
      }

      const allJobs = await jobsResponse.json();

      // Filter jobs that match the responsible's job_id array
      const jobIds = currentResponsible.job_id || [];
      const responsibleJobs = allJobs
        .filter((job: Job) => jobIds.includes(job.id)) // Filter by job IDs
        .sort((a: Job, b: Job) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime() // Sort by creation date (newest first)
        );

      setJobs(responsibleJobs); // Update jobs state with filtered results
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load jobs';
      console.error('Error in useResponsibleJobs:', errorMessage);
      setError(errorMessage); // Set error message
    } finally {
      setLoading(false); // Update loading state
    }
  };

  // Fetch jobs when the token or user changes
  useEffect(() => {
    if (token && user) {
      fetchResponsibleJobs();
    }
  }, [token, user]);

  // Return the state and a refetch function
  return {
    jobs, // List of jobs assigned to the responsible user
    loading, // Loading state
    error, // Error message, if any
    refetch: fetchResponsibleJobs, // Function to manually refetch jobs
  };
}
